/**
 * "Resolve with Agents" modal — Scene 7 of the Vitalora demo.
 *
 * Takes the pending AI review comments, groups them by mentioned owner,
 * and animates the agent action plan: Slack DM, Wrike task, follow-up
 * schedule. The actual Slack/Wrike side-effects are not wired in this
 * branch — Composio OAuth setup is parallel ops work — but the seed
 * data populates the IntegrationEvent collection so the agent activity
 * feed reads as if these actions ran.
 *
 * Visibility is gated by the caller (currently Comments.tsx) on the
 * Vitalora demo enterprise.
 */
import { CheckCircle } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";

interface UserLike {
  _id: string;
  name?: string | null;
  email?: string;
  avatar?: string;
}

interface CommentLike {
  _id: string;
  description?: string;
  rule?: string;
  mentions?: string[];
}

interface OwnerGroup {
  user: UserLike;
  comments: CommentLike[];
}

interface ResolveWithAgentsModalProps {
  open: boolean;
  onClose: () => void;
  comments: CommentLike[];
  users: UserLike[];
  ticketTitle: string;
}

type Step = "splitting" | "notifying" | "creating-tasks" | "done";

const STEP_LABELS = {
  splitting: "Splitting comments by owner…",
  notifying: "Sending Slack DMs…",
  "creating-tasks": "Creating Wrike tasks…",
  done: (count: number) =>
    `All ${count} comments routed. Submitted for final sign-off.`,
} as const;

export function ResolveWithAgentsModal(props: ResolveWithAgentsModalProps) {
  const [step, setStep] = useState<Step>("splitting");

  const ownerGroups: OwnerGroup[] = useMemo(() => {
    const byOwner = new Map<string, OwnerGroup>();
    for (const comment of props.comments) {
      const ownerId = comment.mentions?.[0];
      if (!ownerId) continue;
      const owner = props.users.find((u) => u._id === ownerId);
      if (!owner) continue;
      const existing = byOwner.get(ownerId);
      if (existing) {
        existing.comments.push(comment);
      } else {
        byOwner.set(ownerId, { user: owner, comments: [comment] });
      }
    }
    return Array.from(byOwner.values()).sort(
      (a, b) => b.comments.length - a.comments.length,
    );
  }, [props.comments, props.users]);

  useEffect(() => {
    if (!props.open) return;
    setStep("splitting");
    const t1 = setTimeout(() => setStep("notifying"), 1200);
    const t2 = setTimeout(() => setStep("creating-tasks"), 2600);
    const t3 = setTimeout(() => setStep("done"), 4000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [props.open]);

  return (
    <Dialog.Root open={props.open} onOpenChange={(o) => !o && props.onClose()}>
      <Dialog.Content maxWidth="640px" data-testid="resolve-with-agents-modal">
        <Dialog.Title>Resolve with Agents</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          {props.comments.length} comments on{" "}
          <strong>{props.ticketTitle}</strong>. Splitting by owner, notifying
          via Slack, and creating Wrike tasks.
        </Dialog.Description>

        <Flex direction="column" gap="3" data-testid="resolve-progress">
          <ProgressLine
            state={progressFor(step, "splitting")}
            label="Splitting comments by owner"
          />
          <ProgressLine
            state={progressFor(step, "notifying")}
            label="Sending Slack DMs"
          />
          <ProgressLine
            state={progressFor(step, "creating-tasks")}
            label="Creating Wrike tasks"
          />
        </Flex>

        <Flex
          direction="column"
          gap="3"
          mt="5"
          data-testid="resolve-owner-groups"
        >
          {ownerGroups.map((group) => (
            <article
              key={group.user._id}
              className="rounded border border-puntt-neutral-gray-6 bg-base-white p-3"
            >
              <Text weight="bold" size="2">
                {group.comments.length} fixes →{" "}
                {group.user.name || group.user.email}
              </Text>
              <ul className="mt-2 list-disc pl-5 text-sm">
                {group.comments.map((c) => (
                  <li key={c._id}>{c.rule || c.description?.slice(0, 90)}</li>
                ))}
              </ul>
            </article>
          ))}
        </Flex>

        {step === "done" ? (
          <>
            <article className="border-green-300 bg-green-50 mt-5 rounded border p-3">
              <Text weight="bold" size="2" color="green">
                {STEP_LABELS.done(props.comments.length)}
              </Text>
              <Text as="p" size="1" mt="1">
                Agent follow-up schedule: Slack reminder at 24h, escalation to
                Sarah Chen at 48h, launch-risk flag in ROI dashboard at 72h.
              </Text>
            </article>

            {/* S7 Step 5 — Wrike artifact cards. We render one card per
                owner group so the demoer can point at "the new Wrike
                tasks" without leaving Puntt. The shortIds match the
                seed-mg-wrike-for-vitalora.ts task titles when the real
                Wrike side-effect runs (gated on COMPOSIO_API_KEY); when
                it doesn't, this panel is purely cosmetic. */}
            <div className="mt-5" data-testid="resolve-with-agents-wrike-cards">
              <Text weight="bold" size="2" mb="2" as="div">
                New Wrike tasks created
              </Text>
              <Flex direction="column" gap="2">
                {ownerGroups.map((group, idx) => {
                  const shortId = `492${idx + 1}`;
                  const dueDate = new Date(
                    Date.now() + (idx + 2) * 24 * 60 * 60 * 1000,
                  );
                  return (
                    <article
                      key={`wrike-${group.user._id}`}
                      className="rounded-md border border-puntt-neutral-gray-6 bg-base-white p-3"
                    >
                      <Flex justify="between" align="center">
                        <Text size="2" weight="bold">
                          [#{shortId}] {props.ticketTitle} —{" "}
                          {group.comments.length} fixes for{" "}
                          {group.user.name?.split(" ")[0] || "owner"}
                        </Text>
                        <Text size="1" color="gray">
                          due {dueDate.toLocaleDateString()}
                        </Text>
                      </Flex>
                      <Text as="div" size="1" color="gray" mt="1">
                        Assigned to {group.user.name || group.user.email} ·{" "}
                        {group.comments.length} linked comments · Slack DM
                        delivered
                      </Text>
                    </article>
                  );
                })}
              </Flex>
            </div>
          </>
        ) : (
          <Text as="p" size="1" mt="3" color="gray">
            {STEP_LABELS[step]}
          </Text>
        )}

        <Flex justify="end" gap="3" mt="5">
          <Dialog.Close>
            <Button
              variant="soft"
              data-testid="resolve-with-agents-close"
              onClick={props.onClose}
            >
              Close
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

const STEP_ORDER: Step[] = ["splitting", "notifying", "creating-tasks", "done"];

function progressFor(
  current: Step,
  target: Step,
): "pending" | "running" | "done" {
  const ci = STEP_ORDER.indexOf(current);
  const ti = STEP_ORDER.indexOf(target);
  if (ci > ti) return "done";
  if (ci === ti) return "running";
  return "pending";
}

function ProgressLine(props: {
  state: "pending" | "running" | "done";
  label: string;
}) {
  const { state, label } = props;
  return (
    <Flex align="center" justify="between" gap="3">
      <Flex align="center" gap="3">
        <span
          className={
            state === "done"
              ? "bg-puntt-success-9 inline-block size-3 rounded-full"
              : state === "running"
                ? "bg-blue-500 inline-block size-3 animate-pulse rounded-full"
                : "inline-block size-3 rounded-full bg-puntt-neutral-gray-6"
          }
        />
        <Text
          size="2"
          weight={state === "running" ? "bold" : "regular"}
          className={
            state === "done"
              ? "text-puntt-neutral-gray-10 line-through"
              : undefined
          }
        >
          {label}
        </Text>
      </Flex>
      {state === "done" ? (
        <CheckCircle
          size={18}
          weight="fill"
          className="text-puntt-success-11"
          data-testid="resolve-step-check"
        />
      ) : null}
    </Flex>
  );
}
